import { Alert, Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import { DocumentScanner } from '@/components/DocumentScanner';
import { Icon } from '@/components/Icon/Icon';
import { IconCell } from '@/components/Icon/IconCell';
import { IdmButton } from '@/components/Idmission/IdmButton.styled';
import { IdmCard } from '@/components/Idmission/IdmCard.styled';
import { Screen } from '@/components/Layout/Screen';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { usePreloadOpenCV } from '@/hooks/usePreloadOpenCV';
import { useTranslate } from '@/hooks/useTranslate';
import { useMinorIdentityUpload } from '@/hooks/verification/useMinorIdentityUpload';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectRightStartFeature } from '@/redux/configs/selectors';

import { DocumentType } from '@/types/document';

import { getDataTestId } from '@/utils/browser';
import { PROGRESS_STEPS } from '@/utils/progressSteps';
import { renderHTMLText } from '@/utils/string';
import { isSurinameUser } from '@/utils/user';

import {
  CardWrapper,
  ContentWrapper,
} from './VerifyMinorBirthCertificateScreen.styled';

type VerifyMinorBirthCertificateScreenProps = {
  documentType?: DocumentType.BirthCertificate | DocumentType.FamilyBook;
};

export default function VerifyMinorBirthCertificateScreen({
  documentType = DocumentType.BirthCertificate,
}: VerifyMinorBirthCertificateScreenProps) {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const user = useAppSelector(selectLoggedInUser);
  const [isCaptureStarted, setIsCaptureStarted] = useState(false);
  const { upload, error, resetError } = useMinorIdentityUpload(documentType);
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const isFamilyBook = documentType === DocumentType.FamilyBook;
  const isSuriname = isSurinameUser(user);

  // Start loading OpenCV + WASM eagerly so it's ready when the user taps "Capture with Camera"
  usePreloadOpenCV();

  const handleBack = useCallback(() => {
    navigate(ROUTE.VERIFY_MINOR_ID, { state: { skipRejection: true } });
  }, [navigate]);

  const handleStartCapture = useCallback(() => {
    resetError();
    setIsCaptureStarted(true);
  }, [resetError]);

  useEffect(() => {
    if (isFamilyBook && !isSuriname) {
      navigate(ROUTE.VERIFY_MINOR_ID, { replace: true });
    } else if (user && !enabledRightStartAccount) {
      navigate(ROUTE.MORE_INFORMATION, { replace: true });
    }
  }, [navigate, user, enabledRightStartAccount, isFamilyBook, isSuriname]);

  if (isFamilyBook && !isSuriname) return null;

  if (isCaptureStarted) {
    return (
      <DocumentScanner
        autoCapture={true}
        onConfirm={upload}
        error={error}
        onBack={() => setIsCaptureStarted(false)}
        onRetake={resetError}
      />
    );
  }

  return (
    <Screen
      header={{
        leftSlot: (
          <IconButton
            onClick={handleBack}
            {...getDataTestId(
              isFamilyBook ? 'family-book-back' : 'birth-cert-back',
            )}
          >
            <Icon
              name="arrow-left"
              color={(theme: Theme) => theme.palette.neutral[700]}
            />
          </IconButton>
        ),
        centerSlot: !isFamilyBook && (
          <ProgressDots
            {...PROGRESS_STEPS.livenessVerificationStep}
            activeColor={(theme: Theme) => theme.palette.primaries[400]}
            inactiveColor={(theme: Theme) => theme.palette.grey[300]}
          />
        ),
      }}
      bgColor={(theme: Theme) => theme.palette.grey[50]}
    >
      <ContentWrapper>
        <Typography variant="h3" textAlign="center">
          {t('verification.verifyMinorID.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {renderHTMLText(
            t(
              isFamilyBook
                ? 'verification.verifyMinorID.familyBook.instruction'
                : 'verification.verifyMinorID.birthCertificate.instruction',
            ),
          )}
        </Typography>
        {isFamilyBook && (
          <Alert
            icon={<Icon name="info" />}
            severity="info"
            variant="outlined"
            sx={{ width: '100%' }}
          >
            {t('verification.verifyMinorID.familyBook.cbbReminder')}
          </Alert>
        )}

        <CardWrapper>
          <IdmCard>
            <IconCell
              name="check"
              color={(theme: Theme) => theme.palette.common.white}
              bgColor={(theme: Theme) => theme.palette.primaries[500]}
              size={80}
              iconSize={40}
            />
            <IdmButton
              {...getDataTestId(
                isFamilyBook
                  ? 'family-book-capture-with-camera'
                  : 'birth-cert-capture-with-camera',
              )}
              onClick={handleStartCapture}
              variant="contained"
              size="large"
              fullWidth
            >
              {t('verification.verifyMinorID.captureWithCamera')}
            </IdmButton>
          </IdmCard>
        </CardWrapper>
      </ContentWrapper>
    </Screen>
  );
}
